/**
 * Secure authentication utilities
 * Centralized token management for security
 */

import { clearStudentSession } from "./studentSession";

export const AUTH_SESSION_KEY = "hasActiveSession";

export const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem("token");
  } catch (error) {
    console.error("Error accessing localStorage:", error);
    return null;
  }
};

export const setAuthToken = (token: string): void => {
  try {
    localStorage.setItem("token", token);
  } catch (error) {
    console.error("Error setting token:", error);
  }
};

export const clearAuthData = (): void => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    localStorage.removeItem("role");
    localStorage.removeItem("profilePicture");
    localStorage.removeItem("userTitle");
    localStorage.removeItem("userId");
    localStorage.removeItem("userCode");
    localStorage.removeItem("email");
    localStorage.removeItem("authUserId");
    clearStudentSession();
    sessionStorage.removeItem(AUTH_SESSION_KEY);
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};

export const markAuthSessionActive = (): void => {
  try {
    sessionStorage.setItem(AUTH_SESSION_KEY, "true");
  } catch (error) {
    console.error("Error setting session marker:", error);
  }
};

export const hasActiveAuthSession = (): boolean => {
  try {
    return sessionStorage.getItem(AUTH_SESSION_KEY) === "true";
  } catch (error) {
    console.error("Error reading session marker:", error);
    return false;
  }
};

export const getUserRole = (): string | null => {
  try {
    return localStorage.getItem("role");
  } catch (error) {
    console.error("Error accessing localStorage:", error);
    return null;
  }
};

export const normalizeRole = (role?: string | null): string => {
  if (!role) return "";
  const cleaned = role.trim().toLowerCase();
  if (!cleaned) return "";
  const compact = cleaned.replace(/[\s_-]/g, "");
  switch (compact) {
    case "superadmin":
      return "superadmin";
    case "admin":
    case "schooladmin":
    case "schooladministrator":
    case "administrator":
    case "adminuser":
      return "admin";
    case "headteacher":
      return "headteacher";
    case "teacher":
      return "teacher";
    case "student":
      return "student";
    default:
      return cleaned;
  }
};

export const getUserName = (): string | null => {
  try {
    return localStorage.getItem("userName");
  } catch (error) {
    console.error("Error accessing localStorage:", error);
    return null;
  }
};

export const getPublicDisplayName = (
  fullName?: string | null,
  title?: string | null,
  role?: string | null
): string => {
  const safeName = (fullName || "").trim();
  const normalizedRole = normalizeRole(role || "");

  if (normalizedRole !== "admin") {
    return safeName || "User";
  }

  const lastName = safeName.split(/\s+/).filter(Boolean).pop() || "User";
  const rawTitle = (title || "").trim();
  const honorific = rawTitle ? (rawTitle.endsWith(".") ? rawTitle : `${rawTitle}.`) : "Mr.";
  return `${honorific} ${lastName}`;
};

/**
 * Decode JWT token without verification (client-side only)
 * Note: This does NOT verify the token signature - that should be done server-side
 */
export const decodeToken = (token: string): { id?: string | number; userId?: string | number; role?: string; exp?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = JSON.parse(atob(base64 + padding));
    return decoded;
  } catch (error) {
    return null;
  }
};

export const getTokenRole = (token: string): string => {
  const decoded = decodeToken(token);
  if (!decoded) return "";
  const rawRole = (decoded as any).role ?? (decoded as any).user_role ?? (decoded as any).userRole;
  return normalizeRole(rawRole);
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 < Date.now();
};
